import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Store } from "../utils/storage";
import { postQuery } from "../utils/api";
import { getCategoriesV2, getEvents, getEventDetails, getFeaturedEvents } from "../utils/queries";
import { getValidDate } from "../utils/formatting";

const storage = Store("session");

const State = {
  searchText: "",
  filteredEvents: [],
  featuredEvents: [],
  categories: [],
  selectedCategories: [],
  selectedFromDate: "",
  selectedToDate: "",
  searchResults: [],
  nextToken: null,
  hasMore: false,
  initialLoading: false,
  loading: false,
  loadingCategories: false,
  totalFound: null,
  overrideVerify: false,
};

const buildPayload = ({ text, fromDate, toDate, categories = [], token, includeDates }) => {
  const payload = {};

  if (text) payload.searchInput = text.toLowerCase().trim();
  if (includeDates) {
    payload.fromDate = getValidDate(fromDate).toISOString();
    payload.toDate = getValidDate(toDate).toISOString();
  }
  if (categories.length) payload.categories = categories;
  if (token) payload.tokenInput = token;

  return payload;
};

const handleEventsResponse = (response, set, override = {}) => {
  const eventsData = response?.data?.getEvents;
  if (response.response === 200 && eventsData) {
    const { items = [], nextToken = null, hasMore = false, totalFound = 0 } = eventsData;
    set({ searchResults: items, filteredEvents: items, nextToken, hasMore, totalFound, ...override });
  } else {
    console.error("Event fetch failed:", response.error || response);
    set({ searchResults: [], filteredEvents: [], nextToken: null, hasMore: false, totalFound: 0, ...override });
  }
};

const Actions = (_set, get) => {
  const set = _set;
  const URL = process.env.REACT_APP_ENDPOINT;

  const setText = async (text) => {
    const { selectedCategories, selectedFromDate, selectedToDate } = get();

    set({
      searchText: text,
      initialLoading: true,
    });

    try {
      const payload = buildPayload({
        text,
        fromDate: selectedFromDate,
        toDate: selectedToDate,
        categories: selectedCategories,
        includeDates: !!(selectedFromDate && selectedToDate),
      });
      const response = await postQuery(URL, getEvents, payload);
      handleEventsResponse(response, set, { initialLoading: false });
    } catch (error) {
      console.error("setText error:", error);
      set({ searchResults: [], filteredEvents: [], nextToken: null, hasMore: false, initialLoading: false });
    }
  };

  const fetchMoreSearchResults = async () => {
    const {
      searchText,
      nextToken,
      searchResults,
      filteredEvents,
      loading,
      hasMore,
      selectedCategories,
      selectedFromDate,
      selectedToDate,
    } = get();

    if (!nextToken || !hasMore || loading) return;
    set({ loading: true });

    try {
      const payload = buildPayload({
        text: searchText,
        fromDate: selectedFromDate,
        toDate: selectedToDate,
        categories: selectedCategories,
        token: nextToken,
        includeDates: !!(selectedFromDate && selectedToDate),
      });
      const response = await postQuery(URL, getEvents, payload);
      const eventsData = response?.data?.getEvents;

      if (response.response === 200 && eventsData) {
        const { items, nextToken: newToken, hasMore: newHasMore = false } = eventsData;
        set({
          searchResults: [...searchResults, ...items],
          filteredEvents: [...filteredEvents, ...items],
          nextToken: newToken,
          hasMore: newHasMore,
          loading: false,
        });
      } else {
        console.error("Fetch more failed:", response.error || response);
        set({ loading: false });
      }
    } catch (error) {
      console.error("fetchMoreSearchResults error:", error);
      set({ loading: false });
    }
  };

  const clear = async () => {
    set({
      searchText: "",
      searchResults: [],
      filteredEvents: [],
      nextToken: null,
    });
    await applyFilters();
  };

  const fetchCategories = async () => {
    set({ loadingCategories: true });
    const response = await postQuery(URL, getCategoriesV2, {});
    if (response.response === 200) {
      set({ categories: response.data.getCategoriesV2 || [], loadingCategories: false });
    } else {
      console.error("fetchCategories error:", response.error || response);
      set({ loadingCategories: false });
    }
  };

  const applyFilters = async () => {
    const { selectedFromDate, selectedToDate, selectedCategories, searchText } = get();

    let fromDateCheck = selectedFromDate;
    let toDateCheck = selectedToDate;

    if (selectedFromDate || selectedToDate) {
      const fromDate = new Date(selectedFromDate).getTime();
      const toDate = new Date(selectedToDate).getTime();

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateTodayFormatted = today.getTime();

      if (fromDate < dateTodayFormatted || toDate < dateTodayFormatted) {
        fromDateCheck = null;
        toDateCheck = null;
      }
    }

    set({ initialLoading: true, selectedFromDate: fromDateCheck, selectedToDate: toDateCheck });

    const includeDates = !!(selectedFromDate && selectedToDate);

    try {
      const payload = buildPayload({
        text: searchText,
        fromDate: fromDateCheck,
        toDate: toDateCheck,
        categories: selectedCategories,
        includeDates,
      });

      const response = await postQuery(URL, getEvents, payload);
      handleEventsResponse(response, set, { initialLoading: false });
    } catch (error) {
      console.error("applyFilters error:", error);
      set({ searchResults: [], filteredEvents: [], nextToken: null, hasMore: false, initialLoading: false });
    }
  };

  const fetchEventByKey = async (eventKey) => {
    const URL = process.env.REACT_APP_ENDPOINT;
    set({ initialLoading: true });

    try {
      const payload = { inputEventKey: eventKey };
      const response = await postQuery(URL, getEventDetails, payload);
      const eventData = response?.data?.getEventDetails?.item;

      if (response.response === 200 && eventData) {
        set({
          filteredEvents: [eventData],
          initialLoading: false,
        });
      } else {
        console.error("Failed to fetch event details:", response.error || response);
        set({
          filteredEvents: [],
          initialLoading: false,
        });
      }
    } catch (error) {
      console.error("Error in fetchEventByKey:", error);
      set({
        filteredEvents: [],
        initialLoading: false,
      });
    }
  };

  const fetchFeaturedEvents = async () => {
    const URL = process.env.REACT_APP_ENDPOINT;
    set({ loading: true });

    try {
      const response = await postQuery(URL, getFeaturedEvents, {});
      if (response.response === 200) {
        set({ featuredEvents: response.data.getFeaturedEvents || [], loading: false });
      } else {
        console.error("fetchFeaturedEvents error:", response.error || response);
        set({ featuredEvents: [], loading: false });
      }
    } catch (error) {
      console.error("Error in fetchFeaturedEvents:", error);
      set({ featuredEvents: [], loading: false });
    }
  };

  const resetVerify = (state) => {
    set({ overrideVerify: state });
  };

  const setFilterDates = async (fromDate, toDate) => {
    if (toDate < fromDate) {
      toDate = fromDate;
    }

    set({ selectedFromDate: fromDate, selectedToDate: toDate });
    await applyFilters();
  };

  return {
    setText,
    clear,
    reset: () => set(State),
    fetchCategories,
    applyFilters,
    setSelectedFromDate: (date) => set({ selectedFromDate: new Date(date) }),
    setSelectedToDate: (date) => set({ selectedToDate: new Date(date) }),
    fetchMoreSearchResults,
    fetchEventByKey,
    fetchFeaturedEvents,
    resetVerify,
    setFilterDates,
  };
};

const EventStore = create(
  persist(
    (set, get) => {
      const actions = Actions(set, get);
      return {
        ...State,
        setSearchText: actions.setText,
        clearText: actions.clear,
        reset: actions.reset,
        fetchCategories: actions.fetchCategories,
        applyFilters: actions.applyFilters,
        setSelectedFromDate: actions.setSelectedFromDate,
        setSelectedToDate: actions.setSelectedToDate,
        fetchMoreSearchResults: actions.fetchMoreSearchResults,
        fetchEventByKey: actions.fetchEventByKey,
        fetchFeaturedEvents: actions.fetchFeaturedEvents,
        resetVerify: actions.resetVerify,
        setFilterDates: actions.setFilterDates,
      };
    },
    {
      name: "app.events",
      storage: createJSONStorage(() => storage),
      partialize: (state) => {
        const { hasMore, loading, initialLoading, loadingCategories, nextToken, ...persisted } = state;
        return persisted;
      },
    }
  )
);

export default EventStore;
